import { useRef, useMemo } from "preact/hooks";
import { useDocumentationCard, DOCS } from "./store";
import { Card, CardContent, Box, Typography, Grid, IconButton, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import DescriptionIcon from '@mui/icons-material/Description';
import toast from "react-hot-toast";

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: 8,
  overflow: "hidden",
}));

const Tile = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "130px",
  width: "100%",
  borderRadius: 16,
  background: theme.palette.mode === "dark" ? "#1c1e22" : theme.palette.grey[100],
  border: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
}));

const Thumb = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

export function CardDocumentationComponent() {
  const { project, role, uploadDocumentation, openViewer, loadingCode, docSrcMap, invalidateDocSrc } = useDocumentationCard();
  const isAdmin = role === 'adevs-admin';
  const inputs = useRef({});

  const values = useMemo(() => ({
    "arch-diagram":  project?.archDiagram,
    "model-diagram": project?.modelDiagram,
    "inf-diagram":   project?.infDiagram,
  }), [project]);

  const onPick = (code) => inputs.current[code]?.click();
  const onChange = async (code, e) => {
    const file = e?.target?.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.png$/i.test(file.name)) {
      toast.error("Solo se aceptan imágenes PNG");
      return;
    }
    await uploadDocumentation(code, file);
  };

  return (
    <StyledCard elevation={1}>
      <Box sx={{ 
          py: 1.5, 
          px: 2, 
          bgcolor: "primary.main",
          color: "primary.contrastText",
        }}>
        <Typography variant="h6" color="inherit" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon />
            Documentación del proyecto
        </Typography>
      </Box>
      <CardContent>
        <Grid container spacing={2}>
          {DOCS.map((d) => {
            const hasImg = !!values[d.code];
            const src = hasImg ? docSrcMap[d.code] : null;
            const busy = loadingCode === d.code;

            return (
              <Grid item xs={12} sm={4} key={d.code}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">{d.label}</Typography>
                  <Tile>
                    {!hasImg || !src ? (
                      isAdmin ? (
                        <Tooltip title="Subir PNG">
                          <IconButton color="primary" onClick={() => onPick(d.code)} disabled={busy} sx={{ transform: "scale(2.2)" }}>
                            <AddCircleOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Sin imagen">
                          <span style={{ transform: "scale(2.2)", opacity: 0.6 }}><ImageNotSupportedIcon /></span>
                        </Tooltip>
                      )
                    ) : (
                      <>
                        <Thumb src={src} alt={d.label} onError={() => invalidateDocSrc(d.code)} />
                        <Tooltip title="Ver">
                          <IconButton onClick={() => openViewer(d)} sx={{ position: "absolute", right: 6, bottom: 6, bgcolor: "rgba(0,0,0,.45)" }} size="small">
                            <ZoomInIcon fontSize="small" sx={{ color: 'white' }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {isAdmin && (
                      <input ref={(r) => (inputs.current[d.code] = r)} type="file" accept=".png" style={{ display: "none" }} onChange={(e) => onChange(d.code, e)} />
                    )}
                  </Tile>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </StyledCard>
  );
}